<template>
  <div class="search-container" ref="searchContainer">
    <!-- Search Input -->
    <!-- <div class="search-input-wrapper" :class="{ 'overlay-active': showOverlay }">
      <i class="pi pi-search search-icon"></i>
      <InputText
        ref="searchInput"
        v-model="searchQuery"
        placeholder="Search links..."
        class="search-input"
        @input="handleInput"
        @focus="handleFocus"
        @keydown="handleKeydown"
      />
      <Button
        v-if="searchQuery"
        icon="pi pi-times"
        class="p-button-text p-button-sm clear-button"
        @click="clearSearch"
      />
      <!-- Keyboard shortcut indicator
      <div v-if="!searchQuery" class="shortcut-indicator">
        <kbd>/</kbd>
      </div>
    </div> -->

    <!-- Search Results Overlay -->
    <div v-if="showOverlay" class="search-overlay" @click="closeOverlay">
      <div class="search-results" @click.stop>
        <!-- Recent Searches -->
        <div v-if="showRecentSearches && recentSearches.length > 0" class="recent-searches">
          <h3>{{ $t('searchBar.recent.title') }}</h3>
          <div class="recent-search-items">
            <div
              v-for="search in recentSearches"
              :key="search"
              class="recent-search-item"
              @click="performSearch(search)"
            >
              <i class="pi pi-history"></i>
              <span>{{ search }}</span>
            </div>
          </div>
        </div>

        <!-- Search Results -->
        <div v-if="searchQuery.length > 2 && linkResults.length > 0" class="search-results-content">
          <!-- Links Results -->
          <div v-if="linkResults.length > 0" class="results-section">
            <h3>
              <i class="pi pi-bookmark section-icon"></i>
              {{ $t('searchBar.results.linksHeading', { n: linkResults.length }) }}
            </h3>
            <div class="results-grid">
              <div
                v-for="link in linkResults"
                :key="`link-${link.id}`"
                class="search-result-link-card-wrapper"
                @click="openLink(link)"
              >
                <LinkCard
                  :item="formatLinkForCard(link)"
                  :show-url="true"
                  :show-title="true"
                  :show-image="true"
                  :show-date="true"
                  :show-tags="true"
                  :show-custom-metadata="true"
                  :show-automatic-metadata="true"
                  :show-notes="false"
                />
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="searchQuery.length > 2 && linkResults.length === 0 && !isSearching" class="no-results">
            <i class="pi pi-search"></i>
            <p>{{ $t('searchBar.results.noResults', { query: searchQuery }) }}</p>
          </div>

          <!-- Loading -->
          <div v-if="isSearching" class="search-loading">
            <LinkflareLoader :label="$t('searchBar.results.searching')" size="1.75rem" />
          </div>
        </div>

        <!-- Initial State -->
        <div v-if="searchQuery.length <= 2 && recentSearches.length === 0" class="search-placeholder">
          <i class="pi pi-search"></i>
          <p>{{ $t('searchBar.placeholder.startTyping') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'petite-vue-i18n'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import Tag from 'primevue/tag'
import ApiService from '../services/api.js'
import { useLinkflareAuth } from '../composables/useAuth.js'
import LinkCard from './LinkCard.vue'
import LinkflareLoader from './LinkflareLoader.vue'
import { formatDate as formatDateUtil } from '../utils/metadataFormatters.js'

export default {
  name: 'SearchBar',
  components: {
    InputText,
    Button,
    Badge,
    Tag,
    LinkCard,
    LinkflareLoader
  },
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const { getAuthToken } = useLinkflareAuth()

    // Refs
    const searchInput = ref(null)
    const searchContainer = ref(null)
    const searchQuery = ref('')
    const showOverlay = ref(false)
    const isSearching = ref(false)
    
    // Results
    const linkResults = ref([])
    const recentSearches = ref([])
    
    // Computed
    const showRecentSearches = ref(false)
    
    // Search debounce
    let searchTimeout = null
    
    // Load recent searches from localStorage
    const loadRecentSearches = () => {
      try {
        const saved = localStorage.getItem('linkflare_recent_searches')
        if (saved) {
          recentSearches.value = JSON.parse(saved).slice(0, 5)
        }
      } catch (error) {
        console.error('Error loading recent searches:', error)
      }
    }
    
    // Save recent searches to localStorage
    const saveRecentSearch = (query) => {
      try {
        const searches = [...recentSearches.value]
        
        // Remove existing instance if exists
        const existingIndex = searches.findIndex(s => s.toLowerCase() === query.toLowerCase())
        if (existingIndex > -1) {
          searches.splice(existingIndex, 1)
        }
        
        // Add to beginning
        searches.unshift(query)
        
        // Keep only 5 most recent
        const trimmed = searches.slice(0, 5)
        recentSearches.value = trimmed
        
        localStorage.setItem('linkflare_recent_searches', JSON.stringify(trimmed))
      } catch (error) {
        console.error('Error saving recent search:', error)
      }
    }
    
    // Perform search
    const performSearch = async (query) => {
      if (!query || query.length < 3) {
        linkResults.value = []
        return
      }
      
      searchQuery.value = query
      isSearching.value = true
      
      try {
        const token = await getAuthToken()
        if (!token) return
        
        // Search links
        const linksResponse = await ApiService.searchLinks(query, { limit: 10 }, token)
        
        linkResults.value = linksResponse.links || []
        
        // Save to recent searches
        saveRecentSearch(query)
        
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        isSearching.value = false
      }
    }
    
    // Handle input with debouncing
    const handleInput = (event) => {
      const query = event.target.value
      showRecentSearches.value = query.length === 0
      
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
      
      if (query.length > 2) {
        searchTimeout = setTimeout(() => {
          performSearch(query)
        }, 300)
      } else {
        linkResults.value = []
      }
    }
    
    // Handle focus
    const handleFocus = () => {
      showOverlay.value = true
      showRecentSearches.value = searchQuery.value.length === 0
      
      if (searchQuery.value.length === 0) {
        loadRecentSearches()
      }
    }
    
    // Handle keydown for shortcuts
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        closeOverlay()
      } else if (event.key === 'Enter' && searchQuery.value.length > 2) {
        performSearch(searchQuery.value)
      }
    }
    
    // Global keydown handler for "/" shortcut
    const handleGlobalKeydown = (event) => {
      // Only trigger if not in any editable field or element
      if (event.key === '/') {
        const activeElement = event.target
        const tagName = activeElement.tagName
        
        // Check if we're in an input field, textarea, or content-editable element
        const isInEditableElement = 
          ['INPUT', 'TEXTAREA'].includes(tagName) ||
          activeElement.isContentEditable ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          // Check if we're inside a content-editable container (like TipTap)
          activeElement.closest('[contenteditable="true"]') !== null ||
          // Check for TipTap editor specifically
          activeElement.closest('.ProseMirror') !== null ||
          activeElement.closest('.tiptap') !== null
        
        if (!isInEditableElement) {
          event.preventDefault()
          focusSearch()
        }
      }
    }
    
    // Focus search input
    const focusSearch = async () => {
      await nextTick()
      if (searchInput.value) {
        searchInput.value.$el.focus()
        showOverlay.value = true
        showRecentSearches.value = searchQuery.value.length === 0
        
        if (searchQuery.value.length === 0) {
          loadRecentSearches()
        }
      }
    }
    
    // Close overlay
    const closeOverlay = () => {
      showOverlay.value = false
      showRecentSearches.value = false
    }
    
    // Clear search
    const clearSearch = () => {
      searchQuery.value = ''
      linkResults.value = []
      showRecentSearches.value = true
      loadRecentSearches()
      
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    }
    
    // Open link
    const openLink = (link) => {
      closeOverlay()
      // Owner's own bookmark — open any scheme they saved (mailto:, tel:, app
      // schemes). A stricter http(s)-only guard here only blocks self-XSS while
      // breaking legitimate non-web bookmarks.
      if (link.url) {
        window.open(link.url, '_blank')
      }
    }
    
    // Format link data for LinkCard component
    const formatLinkForCard = (link) => {
      // Transform the API response to match LinkCard expected format
      const formatActionType = (actionType) => {
        if (!actionType) return 'Read'
        // Convert from API format (e.g., 'WATCH') to display format (e.g., 'Watch')
        return actionType.charAt(0).toUpperCase() + actionType.slice(1).toLowerCase()
      }
      
      return {
        id: link.id,
        title: link.title || t('searchBar.card.untitled'),
        url: link.url,
        actionType: formatActionType(link.action_type || link.actionType),
        type: link.type || null,
        image: link.image || null,
        tags: link.tags || [],
        dateAdded: link.added_at || link.addedAt || link.date_added || new Date().toISOString(),
        collectionId: link.collection_id || link.collectionId || null,
        collectionColor: link.collection_color || link.collectionColor || null,
        inInbox: link.in_inbox || link.inInbox || false,
        pinned: link.pinned || false,
        metaData: link.meta_data || link.metaData || link.metadata || {},
        notes: link.notes || ''
      }
    }
    
    // Format date
    const formatDate = (timestamp) => {
      return formatDateUtil(timestamp)
    }
    
    // Handle click outside
    const handleClickOutside = (event) => {
      if (searchContainer.value && !searchContainer.value.contains(event.target)) {
        closeOverlay()
      }
    }
    
    onMounted(() => {
      document.addEventListener('keydown', handleGlobalKeydown)
      document.addEventListener('click', handleClickOutside)
      loadRecentSearches()
    })
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleGlobalKeydown)
      document.removeEventListener('click', handleClickOutside)
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    })
    
    return {
      searchInput,
      searchContainer,
      searchQuery,
      showOverlay,
      isSearching,
      linkResults,
      recentSearches,
      showRecentSearches,
      handleInput,
      handleFocus,
      handleKeydown,
      clearSearch,
      closeOverlay,
      performSearch,
      openLink,
      formatDate,
      formatLinkForCard
    }
  }
}
</script>

<style scoped>
.search-container {
  position: relative;
  width: 100%;
  max-width: 400px;
  z-index: 10000; /* Ensure search container is above overlay */
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  transition: all 0.3s ease;
}

.search-input-wrapper.overlay-active {
  z-index: 10001; /* Above the overlay when active */
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  color: var(--text-secondary);
  z-index: 2;
  pointer-events: none;
  transition: color 0.3s ease;
}

.overlay-active .search-icon {
  color: var(--accent-color);
}

.search-input {
  width: 100% !important;
  padding-left: 2.5rem !important;
  padding-right: 2.5rem !important;
  border: 1px solid var(--border-color) !important;
  border-radius: 8px !important;
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  font-size: 0.9rem !important;
  height: 40px !important;
  transition: all 0.3s ease !important;
}

.search-input:focus {
  border-color: var(--accent-color) !important;
  box-shadow: 0 0 0 2px var(--accent-light) !important;
  outline: none !important;
}

.overlay-active .search-input {
  background: var(--bg-secondary) !important;
  border-color: var(--accent-color) !important;
  box-shadow: 0 0 0 2px var(--accent-light), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.clear-button {
  position: absolute !important;
  right: 0.25rem;
  z-index: 10;
  color: var(--text-secondary) !important;
  padding: 0.25rem !important;
  min-width: unset !important;
  width: 1.5rem !important;
  height: 1.5rem !important;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 50% !important;
}

.clear-button:hover {
  color: var(--text-primary) !important;
  background-color: var(--bg-hover) !important;
}

.overlay-active .clear-button {
  color: var(--accent-color) !important;
  background-color: var(--accent-light) !important;
}

.overlay-active .clear-button:hover {
  background-color: var(--accent-color) !important;
  color: white !important;
}

.shortcut-indicator {
  position: absolute !important;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  pointer-events: none;
}

.shortcut-indicator kbd {
  background: var(--bg-hover);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-secondary);
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  min-width: 1.5rem;
  text-align: center;
  display: inline-block;
  transition: all 0.3s ease;
}

.overlay-active .shortcut-indicator kbd {
  background: var(--accent-light);
  border-color: var(--accent-color);
  color: var(--accent-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
}

.search-results {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 10px 25px var(--shadow-heavy);
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.recent-searches {
  margin-bottom: 1.5rem;
}

.recent-searches h3 {
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.recent-search-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.recent-search-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.recent-search-item:hover {
  background: var(--bg-hover);
  border-color: var(--accent-light);
}

.recent-search-item i {
  color: var(--text-secondary);
}

.recent-search-item span {
  color: var(--text-primary);
  font-weight: 500;
}

.search-results-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.results-section h3 {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-icon {
  width: 1.2em;
  height: 1.2em;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.search-result-link-card-wrapper {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.search-result-link-card-wrapper:hover {
  transform: translateY(-1px);
}

/* Disable pointer events on buttons inside search results to prevent conflicts */
.search-result-link-card-wrapper :deep(.item-actions) {
  pointer-events: none;
  opacity: 0.6;
}

.result-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.result-card:hover {
  background: var(--bg-hover);
  border-color: var(--accent-light);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px var(--shadow-light);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.card-header h4 {
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
  flex: 1;
}

.card-content {
  flex: 1;
}

.card-preview {
  width: 100%;
}

.preview-image {
  margin-bottom: 0.75rem;
}

.preview-image img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.preview-text p {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
}

.card-meta small {
  color: var(--text-secondary);
  font-size: 0.75rem;
}

.no-results,
.search-loading,
.search-placeholder {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
}

.no-results i,
.search-loading i,
.search-placeholder i {
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.search-loading i {
  color: var(--accent-color);
  opacity: 1;
}

.no-results p,
.search-loading p,
.search-placeholder p {
  margin: 0;
  font-size: 0.95rem;
}

@media (max-width: 768px) {
  .search-overlay {
    padding-top: 5vh;
  }
  
  .search-results {
    width: 95%;
    padding: 1rem;
  }
  
  .results-grid {
    grid-template-columns: 1fr;
  }
  
  .card-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>